import React, { useContext, useState } from "react";
import { Lock, CheckCircle2, Clock, BarChart3 } from "lucide-react";
import { LearnCtx, levelLabel } from "../lib/learn";
import { ResourcesShelf } from "./FileDownload";
import { NotesExport } from "./NotesExport";
import { CertificateButton } from "./Certificate";

const Header = ({ section, learn, accent, beacon }) => {
  const lessons = section.items.filter((it) => it.type !== "artwork");
  const n = lessons.length;
  const done = learn ? lessons.filter((it) => learn.done.has(it.id)).length : 0;
  return (
    <div className="mb-3 space-y-2" data-testid={`course-header-${section.id}`}>
      {(section.level || section.est_time) && <div className="flex flex-wrap gap-3 font-body text-[11px] text-zinc-400">
        {section.level && <span className="inline-flex items-center gap-1"><BarChart3 size={11} />{levelLabel(section.level)}</span>}
        {section.est_time && <span className="inline-flex items-center gap-1"><Clock size={11} />{section.est_time}</span>}
      </div>}
      {learn && n > 0 && (
        <div data-testid={`course-progress-${section.id}`}>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${(done / n) * 100}%`, background: accent }} /></div>
          <div className="font-body text-[11px] text-zinc-400 mt-1">{done === n ? "Course complete. Beautifully done." : `${done} of ${n} complete`}</div>
          {done === n && !!beacon?.certificates_enabled && <CertificateButton beacon={beacon} section={section} accent={accent} />}
        </div>
      )}
    </div>
  );
};

// A step in a Path course opens once the previous lesson (artworks aside) is complete.
export const stepOpen = (section, i, learn) => {
  const it = section.items[i];
  const prev = section.items.slice(0, i).reverse().find((p) => p.type !== "artwork" && (!p.locked || p.drip_at));
  if (it.drip_at) return true;
  return !learn || section.layout !== "path" || it.type === "artwork" || !prev || learn.done.has(prev.id) || learn.done.has(it.id) || it.locked;
};

const Path = ({ section, learn, renderItem, accent }) => (
  <ol className="space-y-1">
    {section.items.map((it, i) => {
      const done = learn?.done.has(it.id);
      const open = stepOpen(section, i, learn);
      return (
        <li key={it.id} className="flex gap-3" data-testid={`course-step-${it.id}`}>
          <span className="shrink-0 w-6 h-6 mt-3 rounded-full border flex items-center justify-center font-body text-[11px]" style={{ borderColor: done || open ? accent : "rgba(255,255,255,0.15)", color: done ? "#0A0A0A" : accent, background: done ? accent : "transparent" }}>
            {done ? <CheckCircle2 size={13} /> : i + 1}
          </span>
          <div className="flex-1 min-w-0">
            {open ? renderItem(it, section) : (
              <div className="flex items-center gap-2 py-3 border-t border-white/5 text-zinc-500 font-body text-sm" data-testid={`course-step-locked-${it.id}`}>
                <Lock size={13} /> <span className="flex-1 truncate">{it.title || `Lesson ${i + 1}`}</span><span className="text-[11px]">Finish the previous lesson to unlock</span>
              </div>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);

const Library = ({ section, learn, renderItem, accent }) => {
  const [sel, setSel] = useState(null);
  const pick = section.items.find((it) => it.id === sel);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {section.items.map((it) => (
          <button key={it.id} type="button" onClick={() => setSel(sel === it.id ? null : it.id)} data-testid={`course-card-${it.id}`}
            className="text-left border p-3 transition-colors hover:bg-white/5" style={{ borderColor: sel === it.id ? accent : "rgba(255,255,255,0.1)" }}>
            <div className="font-body text-sm text-white line-clamp-2">{it.title || "Untitled lesson"}</div>
            <div className="flex items-center gap-2 mt-1.5 font-body text-[10px] uppercase tracking-wide text-zinc-500">
              <span>{it.type}</span>{it.duration && <span>· {it.duration}</span>}
              {learn?.done.has(it.id) && <CheckCircle2 size={12} style={{ color: accent }} className="ml-auto" />}
              {it.locked && <Lock size={11} className="ml-auto" />}
            </div>
          </button>
        ))}
      </div>
      {pick && <div className="mt-3" data-testid="course-library-open">{renderItem(pick, section)}</div>}
    </>
  );
};

// A section shown as a course: header with level, time and progress, then a List, Path or Library layout.
export const CourseView = ({ section, renderItem, accent, encKey, beacon }) => {
  const learn = useContext(LearnCtx);
  return (
    <div className="pt-3 border-t border-white/5" data-testid={`course-${section.id}`} data-layout={section.layout}>
      <Header section={section} learn={learn} accent={accent} beacon={beacon} />
      <NotesExport section={section} accent={accent} />
      <ResourcesShelf section={section} encKey={encKey} accent={accent} visible={(it) => stepOpen(section, section.items.indexOf(it), learn)} />
      {section.layout === "path" ? <Path section={section} learn={learn} renderItem={renderItem} accent={accent} />
        : section.layout === "library" ? <Library section={section} learn={learn} renderItem={renderItem} accent={accent} />
        : section.items.map((it) => renderItem(it, section))}
    </div>
  );
};
